/**
 * THE REQUEST HEADERS THE BROWSER CHAT CLIENT PUTS ON THE WIRE.
 *
 * The chat client (`chatApi.ts` and `embedApi.ts` in the web console, which also
 * serves the embed iframe) sends these to the backend from a browser, cross-origin.
 * A header off the CORS safelist makes the browser send a preflight first, and the
 * request is refused unless the preflight's `Access-Control-Allow-Headers` names
 * every header the request carries. So these names are a contract between two
 * deployables: the client may send nothing outside this list, and the backend's
 * `allowedHeaders` in `app.enableCors(...)` must admit everything inside it.
 *
 * Each side holds its own guard against THIS list, never against the other side's
 * source:
 *
 * - the client types its header maps as {@link EmbedChatRequestHeaders}, so a
 *   header name outside the list does not compile;
 * - the backend's governance spec reads `allowedHeaders` out of `main.ts` and
 *   requires it to cover {@link EMBED_CHAT_REQUEST_HEADER_NAMES}.
 *
 * 🚨 Adding a header is a contract change: add it here, release this package, then
 * the backend admits it and the client sends it. A preflight failure is visible only
 * in a browser — jest, vitest, curl, SSR and the backend's boot smoke all pass
 * without one — which is why both halves check against this list.
 *
 * Spelled as the client spells them. HTTP header names are case-insensitive, so a
 * comparison against another list lower-cases both sides.
 */
export const EMBED_CHAT_REQUEST_HEADERS = {
  contentType: "Content-Type",
  authorization: "Authorization",
  agentId: "x-agent-id",
  sessionId: "x-session-id",
  /**
   * Comma-separated session ids the embed landing screen lists past
   * conversations for. A header and not a query parameter: each id is the
   * capability to read a conversation, and a query string lands in access logs.
   */
  sessionIds: "x-session-ids",
  /**
   * The identity the embed landing screen lists a customer's past conversations
   * for: the host site's own user id, the Nexus customer id, and the HMAC
   * `user_hash` over the user id. Headers and not query parameters, for the same
   * reason as `sessionIds`: the `user_hash` carries no timestamp or nonce, so one
   * written into an access log reads that customer's history for good.
   */
  externalUserId: "x-external-user-id",
  customerId: "x-customer-id",
  userHash: "x-user-hash",
  integrationId: "x-integration-id",
  /**
   * The embedding page's origin, read inside the frame. Telemetry only: it gates
   * nothing, and the client omits it when there is no reading.
   */
  embedAncestorOrigin: "x-embed-ancestor-origin"
} as const;

/** One header name the chat client may send. */
export type EmbedChatRequestHeader =
  (typeof EMBED_CHAT_REQUEST_HEADERS)[keyof typeof EMBED_CHAT_REQUEST_HEADERS];

/**
 * A header map the chat client can build. Every key optional, no index signature:
 * a literal or computed key outside {@link EmbedChatRequestHeader} is a type error.
 */
export type EmbedChatRequestHeaders = Partial<Record<EmbedChatRequestHeader, string>>;

/** Every header name in {@link EMBED_CHAT_REQUEST_HEADERS}, as a list. */
export const EMBED_CHAT_REQUEST_HEADER_NAMES: readonly EmbedChatRequestHeader[] = Object.freeze(
  Object.values(EMBED_CHAT_REQUEST_HEADERS)
);
